/**
 * `v-reveal` — fade a block up as it first enters the viewport.
 *
 * Registered globally so any page can use it without an import. It exists to
 * make long marketing pages feel alive rather than to animate: the movement is
 * 12px over 500ms, it happens once, and it never delays anything a visitor is
 * trying to read.
 *
 * Three rules it keeps:
 *  - Content is visible before the script runs. The element starts at full
 *    opacity and the directive dims it only after it has taken over, so a failed
 *    or slow bundle can never leave a blank page.
 *  - `prefers-reduced-motion` skips the whole thing.
 *  - The observer disconnects after firing.
 *
 * Usage: `<div v-reveal>` or `<div v-reveal="{ delay: 120 }">`
 */
const REVEALED = 'is-revealed'

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  window.matchMedia?.('(prefers-reduced-motion: reduce)').matches

export const reveal = {
  mounted (el, binding) {
    if (prefersReducedMotion() || typeof IntersectionObserver === 'undefined') {
      el.classList.add(REVEALED)
      return
    }

    const delay = Number(binding.value?.delay ?? 0)

    el.classList.add('reveal')
    if (delay) {
      el.style.transitionDelay = `${delay}ms`
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return
          el.classList.add(REVEALED)
          observer.disconnect()
        })
      },
      { rootMargin: '0px 0px -10% 0px', threshold: 0.05 }
    )

    observer.observe(el)
    el._revealObserver = observer
  },

  unmounted (el) {
    el._revealObserver?.disconnect()
    delete el._revealObserver
  },
}

export default reveal
